import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios interceptor to always add token (runs once on module load)
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on auth endpoints - let them handle their own errors
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      console.log('401 error on non-auth endpoint, token may be invalid');
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configure axios defaults on mount
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('jwt_token');
    
    if (!token) {
      console.log('No token found in localStorage');
      setUser(null);
      setLoading(false);
      return;
    }

    console.log('Found token, verifying with /auth/me...');
    
    // Configure axios with token
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      const response = await axios.get(`${API}/auth/me`);
      console.log('Auth verified, user:', response.data?.email);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error.response?.status, error.response?.data);
      // Only clear token if it's actually invalid (401), not for network errors
      if (error.response?.status === 401) {
        console.log('Token invalid, clearing...');
        localStorage.removeItem('jwt_token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } else {
        // Network error or server error - keep the token, maybe server is temporarily down
        console.log('Server error, keeping token for retry');
        // Try to decode the token and get basic user info
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('Using cached user from token:', payload);
          setUser({
            user_id: payload.user_id,
            email: payload.email,
            role: payload.role,
            name: payload.name
          });
        } catch (e) {
          console.error('Failed to decode token:', e);
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    // Store JWT token
    localStorage.setItem('jwt_token', response.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    
    // Check if pending approval
    if (response.data.pending_approval) {
      return response.data;
    }
    
    // Store JWT token
    localStorage.setItem('jwt_token', response.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear JWT token
    localStorage.removeItem('jwt_token');
    delete axios.defaults.headers.common['Authorization'];
    
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}